import { megawinApi } from "api/megawin";
import { useEffect, useRef, useState } from "react";

export const validPhone = (phone) => /^[0-9]{8}$/.test(phone);

export default function useMegawinPurchase(selected, ticketCount) {
  const [phone, setPhoneValue] = useState("");
  const [customer, setCustomer] = useState(null);
  const [payment, setPayment] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [error, setError] = useState("");
  const [action, setAction] = useState(null);
  const version = useRef(0);
  const requestBusy = useRef(false);
  const active = useRef(null);
  useEffect(
    () => () => {
      version.current += 1;
      active.current?.abort();
    },
    []
  );

  const setPhone = (value) => {
    if (value === phone) return;
    version.current += 1;
    active.current?.abort();
    requestBusy.current = false;
    setAction(null);
    setPhoneValue(value);
    setCustomer(null);
    setPayment(null);
    setTickets(null);
    setError("");
  };

  const run = async (kind, work) => {
    if (requestBusy.current) return false;
    const current = version.current;
    const controller = new AbortController();
    active.current = controller;
    requestBusy.current = true;
    setAction(kind);
    setError("");
    const isCurrent = () =>
      current === version.current && !controller.signal.aborted;
    try {
      await work({ signal: controller.signal }, isCurrent);
      return isCurrent();
    } catch (failure) {
      if (isCurrent()) setError(failure.message || "Хүсэлт амжилтгүй боллоо.");
      return false;
    } finally {
      if (isCurrent()) {
        requestBusy.current = false;
        setAction(null);
        active.current = null;
      }
    }
  };

  const refreshCustomer = async (options, isCurrent) => {
    const response = await megawinApi.getCustomer(phone, options);
    if (isCurrent()) setCustomer(response.data);
  };
  const loadCustomer = () => {
    if (!validPhone(phone)) {
      setError("8 оронтой утасны дугаар оруулна уу.");
      return Promise.resolve(false);
    }
    return run("customer", refreshCustomer);
  };
  const buyTickets = () => {
    if (
      !validPhone(phone) ||
      !customer ||
      !selected ||
      !Number.isInteger(ticketCount) ||
      ticketCount < 1 ||
      ticketCount > selected.ticket_balance
    )
      return Promise.resolve(false);
    return run("checkout", async (options, isCurrent) => {
      const response = await megawinApi.checkout(
        [{ section_id: selected.id, count: ticketCount }],
        phone,
        options
      );
      if (!isCurrent()) return;
      if (
        !response.data?.checkoutId ||
        typeof response.data?.qrData !== "string" ||
        !response.data.qrData.trim()
      )
        throw new Error(
          "Төлбөрийн QR мэдээлэл дутуу байна. Дахин оролдоно уу."
        );
      // Invoice metadata is retained, but only qrData is passed to the QR renderer.
      // Some invoice responses omit status; an invoice is pending until verified.
      setPayment({
        ...response.data,
        status: response.data.status || "pending",
      });
      setTickets(null);
      if (response.data.status === "paid")
        await refreshCustomer(options, isCurrent);
    });
  };
  const checkPayment = () => {
    if (!payment?.checkoutId) return Promise.resolve(false);
    return run("payment", async (options, isCurrent) => {
      const response = await megawinApi.checkPayment(
        payment.checkoutId,
        options
      );
      if (!isCurrent()) return;
      if (
        !["pending", "paid", "expired", "failed"].includes(
          response.data?.status
        )
      )
        throw new Error("Төлбөрийн төлөв тодорхойгүй байна. Дахин шалгана уу.");
      setPayment((current) => ({ ...current, ...response.data }));
      if (response.data.status === "paid") {
        setCustomer(null);
        await refreshCustomer(options, isCurrent);
      }
    });
  };
  const checkTickets = (checkoutId) => {
    if (!validPhone(phone) || !checkoutId.trim()) return Promise.resolve(false);
    return run("tickets", async (options, isCurrent) => {
      setTickets(null);
      const response = await megawinApi.checkTickets(
        checkoutId.trim(),
        phone,
        options
      );
      if (!isCurrent()) return;
      if (
        response.data?.status !== "paid" ||
        !Array.isArray(response.data?.tickets)
      )
        throw new Error("Төлөгдсөн захиалгын тасалбарын мэдээлэл ирсэнгүй.");
      setTickets({
        checkoutId: response.data.checkoutId || checkoutId,
        items: response.data.tickets,
      });
    });
  };
  return {
    phone,
    setPhone,
    customer,
    loadCustomer,
    payment,
    setPayment,
    tickets,
    checkTickets,
    error,
    setError,
    requestBusy,
    busy: !!action,
    submitting: action === "checkout",
    checking: action === "payment",
    customerLoading: action === "customer",
    buyTickets,
    checkPayment,
  };
}
